import { mergeAttributes, type Range } from '@tiptap/core';
import { Image as BaseImage } from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';

import { ImageBlockView } from './components/ImageBlockView';

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		imageBlock: {
			setImageBlock: (attributes: { src: string; alt?: string }) => ReturnType;
			setImageBlockAt: (attributes: { src: string; pos: number | Range; alt?: string }) => ReturnType;
			setImageBlockAlign: (align: 'left' | 'center' | 'right') => ReturnType;
			setImageBlockWidth: (width: number) => ReturnType;
		};
	}
}

const Image = BaseImage.extend({
	group: 'block',
});

export const ImageBlock = Image.extend({
	name: 'imageBlock',

	group: 'block',

	defining: true,

	isolating: true,

	draggable: true,

	addAttributes() {
		return {
			src: {
				default: '',
				parseHTML: (element) => element.getAttribute('src'),
				renderHTML: (attributes) => ({
					src: attributes.src,
				}),
			},
			width: {
				default: '100%',
				parseHTML: (element) => {
					const dataWidth = element.getAttribute('data-width');
					if (dataWidth) return dataWidth;

					const styleWidth = element.style.width;
					if (styleWidth) return styleWidth;

					return '100%';
				},
				renderHTML: (attributes) => ({
					'data-width': attributes.width,
					style: `width: ${attributes.width}`,
				}),
			},
			align: {
				default: 'center',
				parseHTML: (element) => element.getAttribute('data-align'),
				renderHTML: (attributes) => ({
					'data-align': attributes.align,
				}),
			},
			alt: {
				default: undefined,
				parseHTML: (element) => element.getAttribute('alt'),
				renderHTML: (attributes) => ({
					alt: attributes.alt,
				}),
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: 'img[src]:not([src^="data:"])',
			},
		];
	},

	renderHTML({ HTMLAttributes }) {
		return ['img', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)];
	},

	addCommands() {
		return {
			setImageBlock:
				(attrs) =>
				({ commands }) => {
					return commands.insertContent([
						{ type: 'imageBlock', attrs: { src: attrs.src, alt: attrs.alt } },
						{ type: 'paragraph', content: '' },
					]);
				},

			setImageBlockAt:
				(attrs) =>
				({ commands }) => {
					return commands.insertContentAt(attrs.pos, {
						type: 'imageBlock',
						attrs: { src: attrs.src, alt: attrs.alt },
					});
				},

			setImageBlockAlign:
				(align) =>
				({ commands }) =>
					commands.updateAttributes('imageBlock', { align }),

			setImageBlockWidth:
				(width) =>
				({ commands }) =>
					commands.updateAttributes('imageBlock', {
						width: `${Math.max(0, Math.min(100, width))}%`,
					}),
		};
	},

	addNodeView() {
		return ReactNodeViewRenderer(ImageBlockView);
	},
});

export default ImageBlock;
